<script setup>
import { ref, onMounted, computed } from 'vue';
import { Button, Card, Switch, message, Alert } from 'ant-design-vue';
import {
    getConsultationTicketSettings,
    updateConsultationTicketSettings
} from '@/api/consultation/consultationApi';
import { usePermission } from '@/composables/usePermission';

const { canView, canUpdate } = usePermission('ConsultationTicketSettings');

const loading = ref(false);
const saving = ref(false);
const wealthosRequireTicket = ref(false);
const growthServiceActive = ref(false);
const updatedAt = ref('');

const enforced = computed(
    () => wealthosRequireTicket.value && growthServiceActive.value
);

const load = async () => {
    if (!canView.value) return;
    loading.value = true;
    try {
        const res = await getConsultationTicketSettings();
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        const data = res.data || {};
        wealthosRequireTicket.value = !!data.wealthos_require_ticket;
        growthServiceActive.value = !!data.growth_service_active;
        updatedAt.value = data.updated_at || '';
    } catch (e) {
        message.error(e?.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    if (!canUpdate.value) return;
    saving.value = true;
    try {
        const res = await updateConsultationTicketSettings({
            wealthos_require_ticket: wealthosRequireTicket.value,
            growth_service_active: growthServiceActive.value
        });
        if (res.status !== 1) throw new Error(res.message || '儲存失敗');
        const data = res.data || {};
        wealthosRequireTicket.value = !!data.wealthos_require_ticket;
        growthServiceActive.value = !!data.growth_service_active;
        updatedAt.value = data.updated_at || '';
        message.success('已儲存');
    } catch (e) {
        message.error(e?.message || '儲存失敗');
    } finally {
        saving.value = false;
    }
};

onMounted(load);
</script>

<template>
    <div class="ticket-settings-page pb-10">
        <Card :bordered="false" class="admin-panel" :loading="loading">
            <div class="ticket-head">
                <div>
                    <h2>諮詢卷開關</h2>
                    <p class="ticket-desc">
                        WealthOS 與世界樹（balance_sheet_booking）兩邊都開，預約才會強制持卷。
                    </p>
                </div>
                <Button v-if="canUpdate" type="primary" :loading="saving" @click="save">儲存</Button>
            </div>

            <Alert
                class="ticket-alert"
                type="info"
                show-icon
                :message="
                    enforced
                        ? '目前強制查卷：預約會占用諮詢卷；取消退卷；完成核銷。'
                        : '目前不強制：預約流程不查卷（預設）。'
                "
            />

            <div class="ticket-fields">
                <label class="ticket-switch">
                    <span>
                        <strong>WealthOS 要求持卷</strong>
                        <small>本系統開關</small>
                    </span>
                    <Switch v-model:checked="wealthosRequireTicket" :disabled="!canUpdate" />
                </label>
                <label class="ticket-switch">
                    <span>
                        <strong>世界樹服務已啟用</strong>
                        <small>對應 growth inventory_item_services.balance_sheet_booking；請與世界樹後台同步後勾選</small>
                    </span>
                    <Switch v-model:checked="growthServiceActive" :disabled="!canUpdate" />
                </label>
            </div>

            <p v-if="updatedAt" class="ticket-meta">上次更新：{{ updatedAt }}</p>
        </Card>
    </div>
</template>

<style scoped>
.ticket-settings-page {
    max-width: 720px;
}
.ticket-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;
}
.ticket-head h2 {
    margin: 0 0 4px;
    font-size: 18px;
}
.ticket-desc {
    margin: 0;
    color: rgba(0, 0, 0, 0.55);
    font-size: 13px;
}
.ticket-alert {
    margin-bottom: 20px;
}
.ticket-fields {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ticket-switch {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
.ticket-switch span {
    display: flex;
    flex-direction: column;
    gap: 2px;
}
.ticket-switch small {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}
.ticket-meta {
    margin: 16px 0 0;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
}
</style>
